
import React from 'react';
import { Card } from "@/components/ui/card";
import { Brain, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';

interface RetentionAnalysisProps {
  performanceData: PerformanceData[];
}

export const RetentionAnalysis: React.FC<RetentionAnalysisProps> = ({ performanceData }) => {
  const { isMobile } = useIsMobile();
  
  const retentionData = React.useMemo(() => {
    if (!performanceData || performanceData.length === 0) {
      return { retention: [], retentionPoints: [] };
    }

    // Group by subject
    const subjectData = performanceData.reduce((acc, curr) => {
      if (!curr.subject) return acc;
      
      if (!acc[curr.subject]) {
        acc[curr.subject] = [];
      }
      acc[curr.subject].push({
        date: new Date(curr.created_at),
        score: curr.score || 0
      });
      return acc;
    }, {} as Record<string, Array<{ date: Date; score: number }>>);

    // Calculate retention rates
    const retention = Object.entries(subjectData).map(([subject, attempts]) => {
      if (attempts.length === 0) return null;
      
      attempts.sort((a, b) => a.date.getTime() - b.date.getTime());
      
      const initialScore = attempts[0].score;
      const latestScore = attempts[attempts.length - 1].score;
      const daysDiff = Math.max(1, (attempts[attempts.length - 1].date.getTime() - attempts[0].date.getTime()) / (1000 * 60 * 60 * 24));
      
      const retentionRate = initialScore > 0 ? Math.min(100, Math.max(0, (latestScore / initialScore) * 100)) : 0;
      
      const nextReview = new Date();
      nextReview.setDate(nextReview.getDate() + Math.max(1, Math.floor(7 * (retentionRate / 100))));

      return {
        topic: subject,
        retentionRate,
        nextReviewDue: nextReview.toISOString()
      };
    }).filter(Boolean);

    // Generate retention curve points
    const retentionPoints = Array.from({ length: 30 }, (_, i) => {
      const days = i + 1;
      const avgRetention = retention.reduce((sum, item) => 
        sum + (item?.retentionRate || 0), 0) / Math.max(1, retention.length);
      
      const expectedRetention = Math.min(100, Math.max(0, 100 * Math.exp(-days / (Math.max(1, avgRetention) / 20))));
      
      return {
        days,
        retention: Math.min(100, Math.max(0, avgRetention || 0)),
        expected: expectedRetention
      };
    });

    return { retention, retentionPoints };
  }, [performanceData]);

  if (!retentionData) {
    return (
      <Card className={isMobile ? "p-4" : "p-6"}>
        <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-semibold mb-4 flex items-center gap-2`}>
          <Brain className="w-5 h-5 text-purple-500" />
          Retention Analysis
        </h2>
        <p className="text-gray-500">Complete more quizzes to see your retention analysis.</p>
      </Card>
    );
  }

  const fontSize = isMobile ? {
    title: "text-base",
    subtitle: "text-sm",
    label: "text-xs"
  } : {
    title: "text-xl",
    subtitle: "text-base",
    label: "text-sm"
  };

  return (
    <Card className={isMobile ? "p-4" : "p-6"}>
      <h2 className={`${fontSize.title} font-semibold mb-6 flex items-center gap-2`}>
        <Brain className="w-5 h-5 text-purple-500" />
        Retention Analysis
      </h2>
      <div className="h-[300px] mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={retentionData.retentionPoints}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis 
              dataKey="days" 
              label={{ 
                value: 'Days', 
                position: 'bottom', 
                offset: -5,
                fontSize: isMobile ? 10 : 12
              }}
              tick={{ fontSize: isMobile ? 10 : 12 }}
            />
            <YAxis
              domain={[0, 100]}
              label={{ 
                value: 'Retention Rate (%)', 
                angle: -90, 
                position: 'insideLeft',
                offset: 10,
                fontSize: isMobile ? 10 : 12
              }}
              tick={{ fontSize: isMobile ? 10 : 12 }}
            />
            <Tooltip
              formatter={(value: number) => [`${value.toFixed(1)}%`, 'Retention Rate']}
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #ccc',
                fontSize: isMobile ? 10 : 12
              }}
            />
            <ReferenceLine y={70} stroke="#10B981" strokeDasharray="3 3" label={{
              value: "Target Retention (70%)",
              fontSize: isMobile ? 10 : 12,
              fill: "#10B981"
            }} />
            <Line
              type="monotone"
              dataKey="expected"
              stroke="#8B5CF6"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Expected Retention"
            />
            <Line
              type="monotone"
              dataKey="retention"
              stroke="#10B981"
              strokeWidth={2}
              name="Your Retention"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-4">
        <h3 className={`font-medium ${fontSize.subtitle} text-gray-700`}>Subject Review Schedule</h3>
        <div className="grid gap-4">
          {retentionData.retention.map((item) => (
            <div key={item.topic} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-purple-500" />
                <span className="font-medium">{item.topic}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className={`${fontSize.label} px-2 py-1 rounded-full ${
                  item.retentionRate >= 70 ? 'bg-green-100 text-green-800' :
                  item.retentionRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {item.retentionRate.toFixed(1)}% retained
                </span>
                <span className={`${fontSize.label} text-gray-500`}>
                  Review: {new Date(item.nextReviewDue).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
